// /admin/people: everyone with a Morsel account, filtered, searched and sorted on the server.
// Click a column heading to sort by it; click again to flip the order.
import { useNavigate } from 'react-router-dom'

import { getPeople } from '../../../api/adminV2'
import { Chips, ListSearch, Pager, PAGE_SIZE, useListParams } from '../list'
import { formatDate, LoadError, Loading, Pill, useLoad } from '../ui'

const FILTERS = [
  ['all', 'Everyone'],
  ['paying', 'Paying'],
  ['free_pro', 'Free Pro'],
  ['lapsed', 'Lapsed'],
  ['free', 'Free'],
  ['former', 'Former Pro'],
  ['ending', 'Ending soon'],
  ['paused', 'Paused'],
  ['expired', 'Expired'],
  ['closed', 'Closed'],
  ['unconfirmed', 'Email not confirmed'],
]

// Sortable columns: the order a first click gives (biggest/newest first, names A to Z)
const SORTS = {
  name: { first: 'asc', asc: 'A to Z', desc: 'Z to A' },
  paid_through: { first: 'asc', asc: 'ends soonest first', desc: 'ends latest first' },
  recipes: { first: 'desc', asc: 'fewest recipes first', desc: 'most recipes first' },
  groups: { first: 'desc', asc: 'fewest groups first', desc: 'most groups first' },
  last_active: {
    first: 'desc',
    asc: 'least recently active first',
    desc: 'most recently active first',
  },
  joined: { first: 'desc', asc: 'oldest first', desc: 'newest first' },
}

const SortHead = ({ label, column, sort, dir, onSort, className }) => {
  const active = sort === column
  return (
    <th
      className={className}
      aria-sort={active ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <button
        type="button"
        className={`a-sort${active ? ' a-on' : ''}`}
        onClick={() =>
          onSort(column, active ? (dir === 'asc' ? 'desc' : 'asc') : SORTS[column].first)
        }
      >
        {label}
        <span className="a-arrow" aria-hidden="true">
          {active ? (dir === 'asc' ? '▲' : '▼') : '↕'}
        </span>
      </button>
    </th>
  )
}

const People = () => {
  const navigate = useNavigate()
  const { read, page, update, key } = useListParams({ filter: 'all', sort: 'joined', dir: 'desc' })
  const filter = read('filter')
  const q = read('q')
  const sort = SORTS[read('sort')] ? read('sort') : 'joined'
  const dir = read('dir') === 'asc' ? 'asc' : 'desc'

  const { data, error, loading, reload } = useLoad(
    () => getPeople({ filter, q: q || undefined, sort, dir, page, size: PAGE_SIZE }),
    [key]
  )
  const onSort = (column, direction) => update({ sort: column, dir: direction })
  const head = { sort, dir, onSort }

  const rows = data?.data || []

  return (
    <>
      <div className="a-head">
        <div>
          <h1>People</h1>
          <p className="a-sub">
            Everyone with a Morsel account. Click a person to see and change their account.
          </p>
        </div>
      </div>
      <section className="a-panel">
        <Chips
          options={FILTERS}
          value={filter}
          counts={data?.counts || {}}
          onChange={(value) => update({ filter: value })}
        />
        <div className="a-toolbar">
          <ListSearch
            value={q}
            placeholder="Search name, email, phone or account #"
            onChange={(value) => update({ q: value }, { replace: !!q })}
          />
          {loading && data ? <span className="a-muted a-small-text">Loading…</span> : null}
        </div>
        {error ? (
          <LoadError error={error} onRetry={reload} />
        ) : !data ? (
          <Loading />
        ) : (
          <>
            <div className="a-table-wrap">
              <table className="a-table">
                <thead>
                  <tr>
                    <SortHead label="Person" column="name" {...head} />
                    <th>Account</th>
                    <th>Plan</th>
                    <SortHead label="Paid through" column="paid_through" {...head} />
                    <SortHead label="Recipes" column="recipes" className="a-num-cell" {...head} />
                    <SortHead label="Groups" column="groups" className="a-num-cell" {...head} />
                    <SortHead label="Last active" column="last_active" {...head} />
                    <SortHead label="Joined" column="joined" {...head} />
                  </tr>
                </thead>
                <tbody>
                  {rows.length ? (
                    rows.map((p) => (
                      <tr
                        className="a-click"
                        key={p.id}
                        onClick={() => navigate(`/admin/people/${p.id}`)}
                      >
                        <td className="a-who">
                          <b>{p.fullname || '(no name)'}</b>
                          <span>{p.email}</span>
                        </td>
                        <td>
                          <Pill status={p.status_key} />
                        </td>
                        <td>{p.plan_label || 'None'}</td>
                        <td className="a-nowrap">
                          {p.end_subscription &&
                          ['paying', 'free_pro', 'lapsed', 'paused', 'expired'].includes(
                            p.status_key
                          ) ? (
                            formatDate(p.end_subscription)
                          ) : (
                            <span className="a-muted">n/a</span>
                          )}
                        </td>
                        <td className="a-num-cell">{Number(p.recipe_count) || 0}</td>
                        <td className="a-num-cell">{Number(p.group_count) || 0}</td>
                        <td className="a-nowrap">
                          {p.last_active ? (
                            formatDate(p.last_active)
                          ) : (
                            <span className="a-muted">n/a</span>
                          )}
                        </td>
                        <td className="a-nowrap">{formatDate(p.created_at)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="8" className="a-muted">
                        {q ? `Nobody matches "${q}" here.` : 'Nobody matches this filter.'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <Pager
              data={data}
              page={page}
              note={SORTS[sort][dir]}
              onPage={(p) => update({ page: p })}
            />
          </>
        )}
      </section>
    </>
  )
}

export default People
